<!--
  CollapsibleSection.vue — expandable/collapsible wrapper for report sub-sections.
  Used to de-clutter long reports without hiding content permanently.

  Props:
    title        — section heading text
    icon         — optional icon name for the heading
    defaultOpen  — start expanded (default true)
    badge        — optional badge text (e.g. score, count)
    badgeTone     — 'accent' | 'warn' | 'danger' | 'neutral' (default 'neutral')

  Slots:
    default      — section content (visible when expanded)

  Usage:
    <CollapsibleSection title="Core Web Vitals" icon="activity" :default-open="false" badge="87" badge-tone="accent">
      <p>LCP: 1.8s, CLS: 0.04...</p>
    </CollapsibleSection>
-->
<template>
  <div class="collapsible-section card p-5" :class="{ 'is-open': open }">
    <button
      type="button"
      class="w-full flex items-center gap-3 text-left"
      @click="open = !open"
      :aria-expanded="open"
    >
      <Icon v-if="icon" :name="icon" :size="18" classes="text-ink-500 shrink-0" />
      <span class="flex-1 text-sm font-semibold text-ink-900 tracking-tight">{{ title }}</span>
      <span
        v-if="badge"
        class="inline-flex items-center px-2 py-0.5 rounded-full text-2xs font-semibold"
        :class="badgeClasses"
      >{{ badge }}</span>
      <span class="collapsible-chevron shrink-0">
        <Icon name="chevron-down" :size="16" classes="text-ink-400 transition-transform duration-200" />
      </span>
    </button>
    <Transition name="collapse">
      <div v-show="open" class="collapsible-body">
        <div class="pt-4">
          <slot />
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import Icon from './Icon.vue';

const props = defineProps({
  title:       { type: String, required: true },
  icon:        { type: String, default: '' },
  defaultOpen: { type: Boolean, default: true },
  badge:       { type: [String, Number], default: '' },
  badgeTone:   { type: String, default: 'neutral' },
});

const open = ref(props.defaultOpen);

const badgeClasses = {
  accent:  'bg-accent-50 text-accent-700',
  warn:    'bg-warn-50 text-warn-700',
  danger:  'bg-danger-50 text-danger-700',
  neutral: 'bg-ink-100 text-ink-600',
}[props.badgeTone] || 'bg-ink-100 text-ink-600';
</script>

<style scoped>
.collapsible-chevron :deep(svg) {
  transition: transform 0.25s cubic-bezier(0.22, 0.61, 0.36, 1);
}
.is-open .collapsible-chevron :deep(svg) {
  transform: rotate(180deg);
}

/* Vue <Transition name="collapse"> */
.collapse-enter-active,
.collapse-leave-active {
  transition: max-height 0.35s cubic-bezier(0.22, 0.61, 0.36, 1),
              opacity 0.3s ease,
              margin 0.35s cubic-bezier(0.22, 0.61, 0.36, 1);
  overflow: hidden;
}
.collapse-enter-from,
.collapse-leave-to {
  max-height: 0;
  opacity: 0;
}
.collapse-enter-to,
.collapse-leave-from {
  max-height: 2000px;
}
.collapsible-body {
  overflow: hidden;
}
</style>
